import { useEffect, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import axios from "axios";
import { readStaleSessionCache, writeSessionCache } from "@/lib/sessionCache";
import { SkeletonTable } from "@/components/Skeletons";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, FileText, Upload, Trash2, Link as LinkIcon, ExternalLink, Copy, Check, X, Clock, FolderOpen, Play, MessageSquare, Video, File, Users, Mail, Edit, Send, Loader2, Eye } from "lucide-react";
import { useYear } from "@/contexts/YearContext";
import FileDropZone from "@/components/FileDropZone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  const trimmed = url.trim();
  const ytMatch = trimmed.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?|shorts)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const tellaMatch = trimmed.match(/tella\.tv\/video\/([^/?#]+)/i);
  if (tellaMatch) return `https://www.tella.tv/video/${tellaMatch[1]}/embed?b=0&title=0&a=1&loop=0&t=0&muted=0&wt=0`;
  const loomMatch = trimmed.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/i);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  const vimeoMatch = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return null;
};

const getStatusColor = (stato) => {
  switch (stato) {
    case "accettato":
      return "status-green";
    case "rifiutato":
    case "Perso":
    case "Sparito":
      return "status-red";
    default:
      return "status-yellow";
  }
};

const formatStato = (stato) => {
  // Formatta per la visualizzazione mantenendo i valori db in minuscolo
  switch (stato) {
    case "accettato":
      return "Accettato";
    case "rifiutato":
      return "Rifiutato";
    case "Perso":
      return "Perso";
    case "Sparito":
      return "Sparito";
    default:
      return stato; // Mantieni altri stati come sono (es. "In Attesa Cliente")
  }
};

export default function OffersPage({ user, logout }) {
  const { selectedYear } = useYear();
  const _cachedAttivi = readStaleSessionCache(`offers_attivi_${selectedYear}`);
  const _cachedInconclusi = readStaleSessionCache(`offers_inconclusi_${selectedYear}`);
  const _cachedAux = readStaleSessionCache(`offers_aux_${selectedYear}`);
  const { mutate } = useSWRConfig();

  // SWR keys
  const attiviKey = `/offers?year=${selectedYear}&light=true&stato_filter=attivi`;
  const inconclusiKey = `/offers?year=${selectedYear}&light=true&stato_filter=inconclusi`;
  const interviewsKey = `/interviews?year=${selectedYear}&light=true`;
  const marketingKey = `/marketing-docs`;
  const settingsKey = `/settings`;

  const [activeTab, setActiveTab] = useState("attivi");
  const [inconclusiLoaded, setInconclusiLoaded] = useState(!!_cachedInconclusi);

  const { data: swrAttivi } = useSWR(attiviKey, { fallbackData: _cachedAttivi });
  // Inconclusi lazy: chiamato solo quando activeTab === "inconclusi" o cache già presente
  const { data: swrInconclusi } = useSWR(
    activeTab === "inconclusi" || inconclusiLoaded ? inconclusiKey : null,
    { fallbackData: _cachedInconclusi }
  );
  const { data: swrInterviews } = useSWR(interviewsKey, { fallbackData: _cachedAux?.interviews });
  const { data: swrMarketing } = useSWR(marketingKey, { fallbackData: _cachedAux?.marketingDocs });
  const { data: swrSettings } = useSWR(settingsKey, { fallbackData: _cachedAux?.settings });

  const [offersAttivi, setOffersAttivi] = useState(swrAttivi || _cachedAttivi || []);
  const [offersInconclusi, setOffersInconclusi] = useState(swrInconclusi || _cachedInconclusi || []);
  const [interviews, setInterviews] = useState(swrInterviews || _cachedAux?.interviews || []);
  const [marketingDocs, setMarketingDocs] = useState(swrMarketing || _cachedAux?.marketingDocs || []);
  const [settings, setSettings] = useState(swrSettings || _cachedAux?.settings || { youtube_link: "" });
  const [loading, setLoading] = useState(!_cachedAttivi && !swrAttivi);
  const [loadingInconclusi, setLoadingInconclusi] = useState(false);
  const [uploadingMarketing, setUploadingMarketing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMarketingDialogOpen, setIsMarketingDialogOpen] = useState(false);
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isClientDetailOpen, setIsClientDetailOpen] = useState(false);
  const [isVideoProssimiDialogOpen, setIsVideoProssimiDialogOpen] = useState(false);
  const [selectedOfferNote, setSelectedOfferNote] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedClientDetail, setSelectedClientDetail] = useState(null);
  const [clientDetailForm, setClientDetailForm] = useState({ nome: "", cognome: "", email: "", telefono: "", note: "" });
  const [youtubeLink, setYoutubeLink] = useState("");
  const [youtubeProssimiLink, setYoutubeProssimiLink] = useState("");
  const [copiedLink, setCopiedLink] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    telefono: "",
    email: "",
    interview_id: ""
  });
  const [marketingFormData, setMarketingFormData] = useState({
    nome: "",
    categoria: "Cataloghi"
  });
  const [marketingFile, setMarketingFile] = useState(null);

  // Preview Email Modal state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSending, setPreviewSending] = useState(false);
  const [previewOfferId, setPreviewOfferId] = useState(null);
  const [previewSubject, setPreviewSubject] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [previewTo, setPreviewTo] = useState("");
  const [previewBcc, setPreviewBcc] = useState("");
  const [previewEditMode, setPreviewEditMode] = useState(false);

  // Sync SWR → local state
  useEffect(() => { if (swrAttivi) setOffersAttivi(swrAttivi); }, [swrAttivi]);
  useEffect(() => {
    if (swrInconclusi) {
      setOffersInconclusi(swrInconclusi);
      setInconclusiLoaded(true);
    }
  }, [swrInconclusi]);
  useEffect(() => { if (swrInterviews) setInterviews(swrInterviews); }, [swrInterviews]);
  useEffect(() => { if (swrMarketing) setMarketingDocs(swrMarketing); }, [swrMarketing]);
  useEffect(() => {
    if (swrSettings) {
      setSettings(swrSettings);
      setYoutubeLink(swrSettings?.youtube_link || "");
      setYoutubeProssimiLink(swrSettings?.video_prossimi_passi || "");
    }
  }, [swrSettings]);
  useEffect(() => { if (swrAttivi && loading) setLoading(false); }, [swrAttivi, loading]);

  const fetchData = async () => {
    // Fetch DIRETTO della lista attivi (bypassa cache SWR + localStorage) e aggiorna cache+state.
    // Necessario perché dopo CRUD (create/upload/delete) il backend restituisce dati freschi
    // grazie a _dash_cache_invalidate, ma SWR con keepPreviousData mostrerebbe transient stale.
    try {
      const res = await axios.get(`${API}${attiviKey}`, {
        withCredentials: true,
        headers: { "Cache-Control": "no-cache" },
      });
      setOffersAttivi(res.data);
      // Sincronizza cache SWR + localStorage senza retriggerare fetch
      mutate(attiviKey, res.data, { revalidate: false });
    } catch (e) {
      // Fallback: rivalida via SWR
      mutate(attiviKey);
    }
    // Aux: possono rimanere lazy tramite mutate standard
    mutate(interviewsKey);
    mutate(marketingKey);
    mutate(settingsKey);
  };

  const fetchInconclusi = async () => {
    setLoadingInconclusi(true);
    try {
      const res = await axios.get(`${API}${inconclusiKey}`, {
        withCredentials: true,
        headers: { "Cache-Control": "no-cache" },
      });
      setOffersInconclusi(res.data);
      mutate(inconclusiKey, res.data, { revalidate: false });
      setInconclusiLoaded(true);
    } catch (_) {
      // Fallback SWR
      try { await mutate(inconclusiKey); setInconclusiLoaded(true); } catch (_) {}
    } finally {
      setLoadingInconclusi(false);
    }
  };

  // Al primo click sulla tab "Inconclusi", carica i dati (lazy load via SWR).
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Il fetch parte automaticamente perché la key SWR non è più null
  };

  // Dopo una mutazione, ricarica tab corrente E l'altra tab se già caricata
  // (una modifica sposta l'offerta tra "attivi" e "inconclusi").
  const refreshCurrentTab = async () => {
    await fetchData();
    if (activeTab === "inconclusi" || inconclusiLoaded) {
      await fetchInconclusi();
    }
  };

  // Aggiorna i link quando le settings cambiano
  useEffect(() => {
    if (settings) {
      setYoutubeLink(settings.youtube_link || "");
      setYoutubeProssimiLink(settings.video_prossimi_passi || "");
    }
  }, [settings]);

  // Ricerca intervista nel dialog "Nuovo Cliente per Offerta"
  const [interviewSearchTerm, setInterviewSearchTerm] = useState("");
  const filteredSortedInterviews = interviews
    .filter((i) => {
      if (!interviewSearchTerm.trim()) return true;
      const q = interviewSearchTerm.toLowerCase();
      return `${i.cognome || ""} ${i.nome || ""} ${i.codice || ""} ${i.telefono || ""}`.toLowerCase().includes(q);
    })
    .sort((a, b) => (a.cognome || "").localeCompare(b.cognome || "", "it", { sensitivity: "base" }));

  const handleImportFromInterview = (interviewId) => {
    const interview = interviews.find((i) => i.id === interviewId);
    if (interview) {
      setFormData({
        nome: interview.nome,
        cognome: interview.cognome,
        telefono: interview.telefono,
        email: interview.email || "",
        interview_id: interview.id
      });
      setInterviewSearchTerm(""); // reset ricerca dopo import
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/offers`, formData, { withCredentials: true });
      toast.success("Offerta creata");
      setIsDialogOpen(false);
      resetForm();
      refreshCurrentTab();
    } catch (e) {
      console.error("Error creating offer:", e);
      toast.error("Errore nella creazione");
    }
  };

  const handleUploadOfferta = async (offerId, file, slot = 1) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/offers/${offerId}/upload-offerta?slot=${slot}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (slot === 1) {
        toast.success('Offerta caricata. Clicca "Notifica Offerta" per inviarla al cliente.');
      } else {
        toast.success(`Offerta PDF ${slot} caricata`);
      }
      refreshCurrentTab();
    } catch (e) {
      console.error("Error uploading offerta:", e);
      toast.error("Errore nel caricamento");
    }
  };

  const handleUploadDocumento = async (offerId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/offers/${offerId}/upload-documento`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Documento caricato");
      refreshCurrentTab();
    } catch (e) {
      console.error("Error uploading documento:", e);
      toast.error("Errore nel caricamento");
    }
  };

  const handleDeleteOfferta = async (offerId, slot = 1) => {
    const label = slot === 1 ? "l'offerta PDF" : `l'offerta PDF ${slot}`;
    if (!window.confirm(`Sei sicuro di voler eliminare ${label}?`)) return;
    try {
      await axios.delete(`${API}/offers/${offerId}/offerta?slot=${slot}`, { withCredentials: true });
      toast.success(slot === 1 ? "Offerta PDF eliminata" : `Offerta PDF ${slot} eliminata`);
      refreshCurrentTab();
    } catch (e) {
      console.error("Error deleting offerta:", e);
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDeleteDocumento = async (offerId, docIndex) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo documento?")) return;
    try {
      await axios.delete(`${API}/offers/${offerId}/documento/${docIndex}`, { withCredentials: true });
      toast.success("Documento eliminato");
      refreshCurrentTab();
    } catch (e) {
      console.error("Error deleting documento:", e);
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo cliente?")) return;
    try {
      await axios.delete(`${API}/offers/${offerId}`, { withCredentials: true });
      toast.success("Cliente eliminato");
      refreshCurrentTab();
    } catch (e) {
      console.error("Error deleting offer:", e);
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleNotificaOfferta = async (offerId) => {
    // Apre modal preview con l'HTML brandizzato prima dell'invio
    setPreviewOfferId(offerId);
    setPreviewEditMode(false);
    setPreviewLoading(true);
    setIsPreviewOpen(true);
    try {
      const res = await axios.get(`${API}/offers/${offerId}/notifica-offerta/preview`, { withCredentials: true });
      setPreviewSubject(res.data.subject || "");
      setPreviewHtml(res.data.html || "");
      setPreviewText(res.data.text || "");
      setPreviewTo(res.data.to || "");
      setPreviewBcc(res.data.bcc || "info@showroomingenio.it");
    } catch (e) {
      console.error("Error loading preview:", e);
      const msg = e.response?.data?.detail || "Errore nel caricamento anteprima";
      toast.error(msg);
      setIsPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmSendOfferta = async () => {
    if (!previewOfferId) return;
    setPreviewSending(true);
    try {
      await axios.post(
        `${API}/offers/${previewOfferId}/notifica-offerta`,
        { subject: previewSubject, html: previewHtml, text: previewText },
        { withCredentials: true }
      );
      toast.success("Email di notifica offerta inviata al cliente!");
      setIsPreviewOpen(false);
    } catch (e) {
      console.error("Error sending notifica offerta:", e);
      const msg = e.response?.data?.detail || "Errore nell'invio della notifica";
      toast.error(msg);
    } finally {
      setPreviewSending(false);
    }
  };

  const handleAggiungiAlFunnel = async () => {
    if (!selectedClientDetail) return;
    
    try {
      const data = {
        nome: selectedClientDetail.nome || "",
        cognome: selectedClientDetail.cognome || "",
        email: selectedClientDetail.email || "",
        telefono: selectedClientDetail.telefono || "",
        data: new Date().toLocaleDateString('it-IT')
      };
      
      await axios.post(`${API}/send-to-funnel`, data, { withCredentials: true });
      toast.success("Cliente aggiunto al Funnel!");
    } catch (e) {
      console.error("Error adding to funnel:", e);
      if (e.response?.data?.detail) {
        toast.error(e.response.data.detail);
      } else {
        toast.error("Errore nell'aggiunta al Funnel");
      }
    }
  };

  const handleUploadMarketingDoc = async (e) => {
    e.preventDefault();
    
    // Validazione esplicita
    if (!marketingFormData.nome.trim()) {
      toast.error("Inserisci il nome del documento");
      return;
    }
    if (!marketingFile) {
      toast.error("Seleziona un file PDF");
      return;
    }
    
    setUploadingMarketing(true);
    const formData = new FormData();
    formData.append("nome", marketingFormData.nome.trim());
    formData.append("categoria", marketingFormData.categoria);
    formData.append("file", marketingFile);
    
    try {
      await axios.post(`${API}/marketing-docs`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Documento marketing caricato con successo!");
      setIsMarketingDialogOpen(false);
      setMarketingFormData({ nome: "", categoria: "Cataloghi" });
      setMarketingFile(null);
      refreshCurrentTab();
    } catch (e) {
      console.error("Error uploading marketing doc:", e);
      toast.error("Errore nel caricamento del documento");
    } finally {
      setUploadingMarketing(false);
    }
  };

  const handleDeleteMarketingDoc = async (docId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo documento?")) return;
    try {
      await axios.delete(`${API}/marketing-docs/${docId}`, { withCredentials: true });
      toast.success("Documento eliminato");
      refreshCurrentTab();
    } catch (e) {
      console.error("Error deleting marketing doc:", e);
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleSaveYoutubeLink = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/settings`, { youtube_link: youtubeLink }, { withCredentials: true });
      toast.success("Link video salvato");
      setIsVideoDialogOpen(false);
      refreshCurrentTab();
    } catch (e) {
      console.error("Error saving youtube link:", e);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleSaveVideoProssimiLink = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/settings`, { video_prossimi_passi: youtubeProssimiLink }, { withCredentials: true });
      toast.success("Video Prossimi Step salvato");
      setIsVideoProssimiDialogOpen(false);
      refreshCurrentTab();
    } catch (e) {
      console.error("Error saving video prossimi:", e);
      toast.error("Errore nel salvataggio");
    }
  };

  const openNoteDialog = (offer) => {
    setSelectedOfferNote(offer);
    setIsNoteDialogOpen(true);
  };

  const handleSaveVideoPersonalizzato = async (offerId, videoLink) => {
    try {
      await axios.put(`${API}/offers/${offerId}`, { video_personalizzato: videoLink || null }, { withCredentials: true });
      toast.success("Video personalizzato salvato");
      refreshCurrentTab();
    } catch (e) {
      console.error("Error saving video personalizzato:", e);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleSaveVideoAggiuntivo = async (offerId, videoLink) => {
    try {
      await axios.put(`${API}/offers/${offerId}`, { video_aggiuntivo: videoLink || null }, { withCredentials: true });
      toast.success("Video Aggiuntivo salvato");
      refreshCurrentTab();
    } catch (e) {
      console.error("Error saving video aggiuntivo:", e);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleToggleRiepilogo = async (offerId, field, value, nome) => {
    try {
      await axios.put(`${API}/offers/${offerId}`, { [field]: value }, { withCredentials: true });
      setSelectedClientDetail((d) => (d && d.id === offerId ? { ...d, [field]: value } : d));
      toast.success(`Riepilogo Caratteristiche ${nome} ${value ? "visibile al cliente" : "nascosto"}`);
      refreshCurrentTab();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleSaveTitolo = async (offerId, slot, titolo) => {
    const field = slot === 1 ? "offerta_pdf_titolo" : `offerta_pdf_${slot}_titolo`;
    try {
      await axios.put(`${API}/offers/${offerId}`, { [field]: titolo || "" }, { withCredentials: true });
      toast.success(`Titolo Offerta ${slot} salvato`);
      refreshCurrentTab();
    } catch (e) {
      console.error("Error saving titolo:", e);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleUploadDocPersonalizzato = async (offerId, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await axios.post(`${API}/offers/${offerId}/upload-doc-personalizzato`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Documento personalizzato caricato");
      refreshCurrentTab();
    } catch (e) {
      console.error("Error uploading doc personalizzato:", e);
      toast.error("Errore nel caricamento");
    }
  };

  const handleDeleteDocPersonalizzato = async (offerId) => {
    if (!window.confirm("Eliminare il documento personalizzato?")) return;
    try {
      await axios.delete(`${API}/offers/${offerId}/doc-personalizzato`, { withCredentials: true });
      toast.success("Documento personalizzato eliminato");
      refreshCurrentTab();
    } catch (e) {
      console.error("Error deleting doc personalizzato:", e);
      toast.error("Errore nell'eliminazione");
    }
  };

  const openClientDetail = (offer) => {
    setSelectedClientDetail(offer);
    setClientDetailForm({
      nome: offer.nome || "",
      cognome: offer.cognome || "",
      email: offer.email || "",
      telefono: offer.telefono || "",
      note: offer.note || ""
    });
    setIsClientDetailOpen(true);
  };

  const handleSaveClientDetail = async () => {
    if (!selectedClientDetail) return;
    try {
      await axios.put(`${API}/offers/${selectedClientDetail.id}`, clientDetailForm, { withCredentials: true });
      toast.success("Dati cliente aggiornati");
      setIsClientDetailOpen(false);
      refreshCurrentTab();
    } catch (e) {
      console.error("Error updating client:", e);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const copyMagicLink = (magicLink) => {
    const link = `${window.location.origin}/offer/${magicLink}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(magicLink);
    toast.success("Link copiato!");
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const resetForm = () => {
    setFormData({
      nome: "",
      cognome: "",
      telefono: "",
      email: "",
      interview_id: ""
    });
  };

  // Offerte da mostrare = tab corrente
  const currentOffers = activeTab === "attivi" ? offersAttivi : offersInconclusi;
  const filteredOffers = currentOffers.filter((o) =>
    `${o.nome} ${o.cognome} ${o.email}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Le statistiche mostrano SOLO le offerte "attive" (In Attesa / Accettati)
  // + i "Rifiutati" totali della tab Inconclusi se già caricati.
  const stateCounts = {
    inAttesa: offersAttivi.filter((o) => o.stato === "In Attesa Cliente").length,
    accettati: offersAttivi.filter((o) => ["accettato", "Vinto"].includes(o.stato)).length,
    rifiutati: offersInconclusi.filter((o) => ["rifiutato", "Perso"].includes(o.stato)).length,
  };

  return (
    <PageLayout user={user} logout={logout} testId="offers-page">
        <div className="flex-1 flex flex-col overflow-hidden p-4 lg:p-6">
          {/* Header - Compatto */}
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Consegna Offerte</h1>
              <p className="text-slate-600 text-sm hidden lg:block">Gestisci le offerte per i clienti</p>
            </div>
            <div className="flex gap-3">
              <Dialog open={isMarketingDialogOpen} onOpenChange={setIsMarketingDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="add-marketing-btn">
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Doc. Marketing
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Documenti Marketing</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    {/* Upload form */}
                    <form onSubmit={handleUploadMarketingDoc} className="space-y-4 border-b pb-4">
                      <div>
                        <Label>Nome Documento</Label>
                        <Input
                          value={marketingFormData.nome}
                          onChange={(e) => setMarketingFormData({ ...marketingFormData, nome: e.target.value })}
                          required
                          data-testid="marketing-doc-name"
                        />
                      </div>
                      <div>
                        <Label>Categoria</Label>
                        <Select
                          value={marketingFormData.categoria}
                          onValueChange={(value) => setMarketingFormData({ ...marketingFormData, categoria: value })}
                        >
                          <SelectTrigger data-testid="marketing-doc-category">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Cataloghi">Cataloghi</SelectItem>
                            <SelectItem value="Schede">Schede</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>File PDF</Label>
                        <FileDropZone
                          accept=".pdf,application/pdf"
                          dropLabel="Rilascia il PDF Marketing"
                          onFile={(f) => setMarketingFile(f)}
                          className="block"
                        >
                          <Input
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setMarketingFile(e.target.files[0])}
                            required
                            data-testid="marketing-doc-file"
                          />
                        </FileDropZone>
                      </div>
                      <Button type="submit" className="w-full" data-testid="upload-marketing-btn" disabled={uploadingMarketing}>
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingMarketing ? "Caricamento in corso..." : "Carica Documento"}
                      </Button>
                    </form>
                    {/* List */}
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm text-slate-600">Documenti Caricati</h4>
                      {marketingDocs.length === 0 ? (
                        <p className="text-sm text-slate-500">Nessun documento</p>
                      ) : (
                        marketingDocs.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                            <div>
                              <p className="font-medium text-sm">{doc.nome}</p>
                              <p className="text-xs text-slate-500">{doc.categoria}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMarketingDoc(doc.id)}
                              className="text-rose-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Video Marketing Dialog */}
              <Dialog open={isVideoDialogOpen} onOpenChange={setIsVideoDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="video-marketing-btn">
                    <Play className="w-4 h-4 mr-2" />
                    Video Marketing
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Video Marketing</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveYoutubeLink} className="space-y-4 mt-4">
                    <div>
                      <Label>Link Video (YouTube, Tella, Loom, Vimeo)</Label>
                      <Input
                        value={youtubeLink}
                        onChange={(e) => setYoutubeLink(e.target.value)}
                        placeholder="https://www.tella.tv/video/... oppure YouTube/Loom/Vimeo"
                        data-testid="youtube-link-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Questo video apparirà in prima posizione nel portale di tutti i clienti. Supporta YouTube, Tella, Loom e Vimeo.
                      </p>
                    </div>
                    {youtubeLink && getVideoEmbedUrl(youtubeLink) && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600 mb-2">Anteprima:</p>
                        <div className="aspect-video rounded overflow-hidden bg-slate-900">
                          <iframe
                            src={getVideoEmbedUrl(youtubeLink)}
                            title="Video Anteprima"
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setIsVideoDialogOpen(false)}>
                        Annulla
                      </Button>
                      <Button type="submit" className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white" data-testid="save-video-btn">
                        Salva Link
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Video Prossimi Step Dialog */}
              <Dialog open={isVideoProssimiDialogOpen} onOpenChange={setIsVideoProssimiDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="bg-blue-50 text-[#1e40af]">
                    <Play className="w-4 h-4 mr-2" />
                    Video Prossimi Step
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Video Prossimi Step</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveVideoProssimiLink} className="space-y-4 mt-4">
                    <div>
                      <Label>Link Video (YouTube, Tella, Loom, Vimeo)</Label>
                      <Input
                        value={youtubeProssimiLink}
                        onChange={(e) => setYoutubeProssimiLink(e.target.value)}
                        placeholder="https://www.tella.tv/video/... oppure YouTube/Loom/Vimeo"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Questo video apparirà nella sezione &quot;I Prossimi Passi&quot; dopo l&apos;accettazione dell&apos;offerta. Supporta YouTube, Tella, Loom e Vimeo.
                      </p>
                    </div>
                    {youtubeProssimiLink && getVideoEmbedUrl(youtubeProssimiLink) && (
                      <div className="bg-slate-50 rounded-lg p-4">
                        <p className="text-sm text-slate-600 mb-2">Anteprima:</p>
                        <div className="aspect-video rounded overflow-hidden bg-slate-900">
                          <iframe
                            src={getVideoEmbedUrl(youtubeProssimiLink)}
                            title="Video Prossimi Passi"
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setIsVideoProssimiDialogOpen(false)}>
                        Annulla
                      </Button>
                      <Button type="submit" className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white">
                        Salva Link
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white" data-testid="add-offer-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Cliente
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nuovo Cliente per Offerta</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    {/* Import from interview */}
                    <div>
                      <Label>Importa da Intervista</Label>
                      <Input
                        type="text"
                        placeholder="Cerca per cognome, nome, codice o telefono..."
                        value={interviewSearchTerm}
                        onChange={(e) => setInterviewSearchTerm(e.target.value)}
                        className="mb-2"
                        data-testid="search-interview-input"
                      />
                      <Select onValueChange={handleImportFromInterview}>
                        <SelectTrigger data-testid="select-interview">
                          <SelectValue placeholder={`Seleziona cliente... (${filteredSortedInterviews.length} risultati, ordinati per cognome)`} />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {filteredSortedInterviews.length === 0 ? (
                            <div className="px-2 py-3 text-sm text-slate-500 text-center">
                              Nessun cliente trovato
                            </div>
                          ) : (
                            filteredSortedInterviews.map((interview) => (
                              <SelectItem key={interview.id} value={interview.id}>
                                {interview.cognome} {interview.nome}{interview.codice ? ` — ${interview.codice}` : ""}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nome *</Label>
                        <Input
                          value={formData.nome}
                          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                          required
                          data-testid="offer-nome"
                        />
                      </div>
                      <div>
                        <Label>Cognome *</Label>
                        <Input
                          value={formData.cognome}
                          onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                          required
                          data-testid="offer-cognome"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Telefono *</Label>
                      <Input
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        required
                        data-testid="offer-telefono"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        data-testid="offer-email"
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                        Annulla
                      </Button>
                      <Button type="submit" className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white" data-testid="submit-offer-btn">
                        Crea Cliente
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Stats - Compatte */}
          <div className="grid grid-cols-3 gap-2 lg:gap-4 mb-4 flex-shrink-0">
            <Card className="stat-card">
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <div>
                    <p className="text-xl font-bold text-amber-600">{stateCounts.inAttesa}</p>
                    <p className="text-xs text-slate-500">In Attesa</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-600" />
                  <div>
                    <p className="text-xl font-bold text-emerald-600">{stateCounts.accettati}</p>
                    <p className="text-xs text-slate-500">Accettati</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="stat-card">
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center gap-2">
                  <X className="w-4 h-4 text-rose-600" />
                  <div>
                    <p className="text-xl font-bold text-rose-600">{stateCounts.rifiutati}</p>
                    <p className="text-xs text-slate-500">Rifiutati</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tab Attivi / Inconclusi */}
          <div className="mb-3 flex-shrink-0 flex gap-2 border-b border-slate-200">
            <button
              type="button"
              onClick={() => handleTabChange("attivi")}
              data-testid="tab-attivi"
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === "attivi"
                  ? "border-[#1e40af] text-[#1e40af]"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              Attivi{" "}
              <span className="ml-1 text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                {offersAttivi.length}
              </span>
            </button>
            <button
              type="button"
              onClick={() => handleTabChange("inconclusi")}
              data-testid="tab-inconclusi"
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === "inconclusi"
                  ? "border-[#1e40af] text-[#1e40af]"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              Inconclusi
              {inconclusiLoaded && (
                <span className="ml-1 text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">
                  {offersInconclusi.length}
                </span>
              )}
            </button>
          </div>

          {/* Search - Compatto */}
          <div className="mb-3 flex-shrink-0">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cerca per nome o email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-9"
                data-testid="offers-search"
              />
            </div>
          </div>

          {/* Table con scroll interno */}
          {(activeTab === "attivi" ? loading : loadingInconclusi) ? (
            <SkeletonTable rows={8} cols={6} />
          ) : (
            <div className="flex-1 overflow-hidden bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="offers-table">
              <div className="overflow-auto h-full">
                <table className="w-full min-w-[1100px] data-table">
                  <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr>
                    <th className="whitespace-nowrap">Cliente</th>
                    <th className="whitespace-nowrap">Offerta PDF 1</th>
                    <th className="whitespace-nowrap">Offerta PDF 2</th>
                    <th className="whitespace-nowrap">Offerta PDF 3</th>
                    <th className="whitespace-nowrap">Offerta PDF 4</th>
                    <th className="whitespace-nowrap">Stato</th>
                    <th className="whitespace-nowrap">Magic Link</th>
                    <th className="whitespace-nowrap">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOffers.map((offer) => (
                    <tr key={offer.id} data-testid={`offer-row-${offer.id}`}>
                      <td>
                        <button
                          onClick={() => openClientDetail(offer)}
                          className="font-medium text-blue-600 hover:text-blue-800 hover:underline text-left bg-transparent"
                          data-testid={`client-detail-${offer.id}`}
                        >
                          {offer.nome} {offer.cognome}
                        </button>
                      </td>
                      {[1, 2, 3, 4].map((slot) => {
                        const fileKey = slot === 1 ? "offerta_pdf" : `offerta_pdf_${slot}`;
                        const titoloKey = slot === 1 ? "offerta_pdf_titolo" : `offerta_pdf_${slot}_titolo`;
                        const fileName = offer[fileKey];
                        const titolo = offer[titoloKey] || "";
                        return (
                          <td key={slot}>
                            {fileName ? (
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <a
                                    href={`${API}/files/${fileName}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline text-sm"
                                    data-testid={`view-offerta-${slot}-${offer.id}`}
                                  >
                                    <FileText className="w-4 h-4 inline mr-1" />
                                    Visualizza
                                  </a>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteOfferta(offer.id, slot)}
                                    className="text-rose-600 p-1 h-auto"
                                    data-testid={`delete-offerta-${slot}-${offer.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                                <Input
                                  type="text"
                                  placeholder="Titolo (es. Standard)"
                                  className="text-xs h-6 w-36 px-2"
                                  defaultValue={titolo}
                                  data-testid={`titolo-offerta-${slot}-${offer.id}`}
                                  onBlur={(e) => {
                                    if (e.target.value !== titolo) {
                                      handleSaveTitolo(offer.id, slot, e.target.value);
                                    }
                                  }}
                                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                />
                              </div>
                            ) : (
                              <FileDropZone
                                accept=".pdf"
                                onFile={(f) => handleUploadOfferta(offer.id, f, slot)}
                                dropLabel={`Rilascia PDF Offerta ${slot}`}
                                className="inline-block"
                              >
                                <label className="cursor-pointer" data-testid={`upload-offerta-${slot}-${offer.id}`}>
                                  <span className="text-sm text-slate-500 hover:text-slate-700">
                                    <Upload className="w-4 h-4 inline mr-1" />
                                    Carica
                                  </span>
                                  <input
                                    type="file"
                                    accept=".pdf"
                                    className="hidden"
                                    onChange={(e) => e.target.files[0] && handleUploadOfferta(offer.id, e.target.files[0], slot)}
                                  />
                                </label>
                              </FileDropZone>
                            )}
                          </td>
                        );
                      })}
                      <td>
                        <div className="flex items-center gap-2">
                          <span className={`status-badge ${getStatusColor(offer.stato)}`}>
                            {formatStato(offer.stato)}
                          </span>
                          {offer.note_cliente && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openNoteDialog(offer)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 text-xs px-2 py-1 h-auto"
                              data-testid={`view-note-${offer.id}`}
                            >
                              <MessageSquare className="w-3 h-3 mr-1" />
                              vedi nota
                            </Button>
                          )}
                        </div>
                      </td>
                      {/* Magic Link */}
                      <td>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyMagicLink(offer.magic_link)}
                            data-testid={`copy-link-${offer.id}`}
                            title="Copia link"
                          >
                            {copiedLink === offer.magic_link ? (
                              <Check className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                          <a
                            href={`/offer/${offer.magic_link}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Apri portale cliente"
                          >
                            <Button variant="ghost" size="sm">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </a>
                        </div>
                      </td>
                      {/* Azioni */}
                      <td>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteOffer(offer.id)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                          data-testid={`delete-offer-${offer.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
                {filteredOffers.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    {searchTerm ? "Nessun risultato trovato" : "Nessun cliente presente"}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dialog per visualizzare la nota del cliente */}
          <Dialog open={isNoteDialogOpen} onOpenChange={setIsNoteDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  Nota del Cliente
                </DialogTitle>
              </DialogHeader>
              {selectedOfferNote && (
                <div className="mt-4">
                  <div className="mb-4">
                    <p className="text-sm text-slate-500">Cliente:</p>
                    <p className="font-medium">{selectedOfferNote.nome} {selectedOfferNote.cognome}</p>
                  </div>
                  <div className="mb-4">
                    <p className="text-sm text-slate-500">Stato:</p>
                    <span className={`status-badge ${getStatusColor(selectedOfferNote.stato)}`}>
                      {formatStato(selectedOfferNote.stato)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 mb-2">Nota:</p>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <p className="text-slate-700 whitespace-pre-wrap">{selectedOfferNote.note_cliente}</p>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex justify-end mt-4">
                <Button variant="outline" onClick={() => setIsNoteDialogOpen(false)}>
                  Chiudi
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Dialog per dettagli cliente */}
          <Dialog open={isClientDetailOpen} onOpenChange={setIsClientDetailOpen}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-slate-600" />
                  Dettagli Cliente
                </DialogTitle>
              </DialogHeader>
              {selectedClientDetail && (
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="detail-nome">Nome</Label>
                      <Input
                        id="detail-nome"
                        value={clientDetailForm.nome}
                        onChange={(e) => setClientDetailForm({ ...clientDetailForm, nome: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="detail-cognome">Cognome</Label>
                      <Input
                        id="detail-cognome"
                        value={clientDetailForm.cognome}
                        onChange={(e) => setClientDetailForm({ ...clientDetailForm, cognome: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="detail-email">Email</Label>
                    <Input
                      id="detail-email"
                      type="email"
                      value={clientDetailForm.email}
                      onChange={(e) => setClientDetailForm({ ...clientDetailForm, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="detail-telefono">Cellulare</Label>
                    <Input
                      id="detail-telefono"
                      value={clientDetailForm.telefono}
                      onChange={(e) => setClientDetailForm({ ...clientDetailForm, telefono: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="detail-note">Note</Label>
                    <Textarea
                      id="detail-note"
                      value={clientDetailForm.note}
                      onChange={(e) => setClientDetailForm({ ...clientDetailForm, note: e.target.value })}
                      rows={3}
                      placeholder="Note sul cliente..."
                    />
                  </div>

                  {/* ==================== Materiali di supporto ==================== */}
                  <div className="pt-2 border-t border-slate-200">
                    <p className="text-sm font-semibold text-slate-700 mb-3">Materiali di supporto per il cliente</p>
                    <div className="grid grid-cols-2 gap-4">
                      {/* PDF Prodotto (Doc Marketing personalizzato) */}
                      <div>
                        <Label className="text-xs">PDF Prodotto</Label>
                        {selectedClientDetail.doc_marketing_personalizzato ? (
                          <div className="flex items-center gap-2 mt-1">
                            <a
                              href={`${API}/files/${selectedClientDetail.doc_marketing_personalizzato}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-emerald-600 hover:underline text-sm flex items-center gap-1"
                              data-testid="detail-view-pdf-prodotto"
                            >
                              <File className="w-4 h-4" /> Visualizza
                            </a>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteDocPersonalizzato(selectedClientDetail.id)}
                              className="text-rose-600 p-1 h-auto"
                              data-testid="detail-delete-pdf-prodotto"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <FileDropZone
                            accept=".pdf,.doc,.docx"
                            onFile={(f) => handleUploadDocPersonalizzato(selectedClientDetail.id, f)}
                            dropLabel="Rilascia PDF Prodotto"
                            className="mt-1"
                          >
                            <label className="cursor-pointer inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 border border-dashed border-slate-300 rounded px-3 py-2">
                              <Upload className="w-4 h-4" /> Carica PDF Prodotto
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.doc,.docx"
                                data-testid="detail-upload-pdf-prodotto"
                                onChange={(e) => e.target.files[0] && handleUploadDocPersonalizzato(selectedClientDetail.id, e.target.files[0])}
                              />
                            </label>
                          </FileDropZone>
                        )}
                      </div>

                      {/* PDF Garanzie (altri_documenti) */}
                      <div>
                        <Label className="text-xs">PDF Garanzie</Label>
                        <div className="mt-1 space-y-1">
                          {(selectedClientDetail.altri_documenti || []).map((doc, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                              <a
                                href={`${API}/files/${typeof doc === "string" ? doc : doc.file_name}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-emerald-600 hover:underline text-sm flex items-center gap-1"
                              >
                                <File className="w-4 h-4" /> Doc {idx + 1}
                              </a>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDocumento(selectedClientDetail.id, idx)}
                                className="text-rose-600 p-1 h-auto"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                          <FileDropZone
                            accept=".pdf"
                            onFile={(f) => handleUploadDocumento(selectedClientDetail.id, f)}
                            dropLabel="Rilascia PDF Garanzie"
                          >
                            <label className="cursor-pointer inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 border border-dashed border-slate-300 rounded px-3 py-2">
                              <Upload className="w-4 h-4" /> Carica PDF Garanzie
                              <input
                                type="file"
                                accept=".pdf"
                                className="hidden"
                                data-testid="detail-upload-pdf-garanzie"
                                onChange={(e) => e.target.files[0] && handleUploadDocumento(selectedClientDetail.id, e.target.files[0])}
                              />
                            </label>
                          </FileDropZone>
                        </div>
                      </div>

                      {/* Sost. Video */}
                      <div>
                        <Label className="text-xs">Sost. Video (link YT/Tella/Loom/Vimeo)</Label>
                        <Input
                          type="text"
                          placeholder="https://..."
                          className="text-xs h-8 mt-1"
                          defaultValue={selectedClientDetail.video_personalizzato || ""}
                          data-testid="detail-video-personalizzato"
                          onBlur={(e) => {
                            if (e.target.value !== (selectedClientDetail.video_personalizzato || "")) {
                              handleSaveVideoPersonalizzato(selectedClientDetail.id, e.target.value);
                            }
                          }}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                        />
                        {selectedClientDetail.video_personalizzato && (
                          <span className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                            <Video className="w-3 h-3" /> Attivo
                          </span>
                        )}
                      </div>

                      {/* Video Aggiuntivo */}
                      <div>
                        <Label className="text-xs">Video Aggiuntivo (link YT/Tella/Loom/Vimeo)</Label>
                        <Input
                          type="text"
                          placeholder="https://..."
                          className="text-xs h-8 mt-1"
                          defaultValue={selectedClientDetail.video_aggiuntivo || ""}
                          data-testid="detail-video-aggiuntivo"
                          onBlur={(e) => {
                            if (e.target.value !== (selectedClientDetail.video_aggiuntivo || "")) {
                              handleSaveVideoAggiuntivo(selectedClientDetail.id, e.target.value);
                            }
                          }}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                        />
                        {selectedClientDetail.video_aggiuntivo && (
                          <span className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                            <Play className="w-3 h-3" /> OK
                          </span>
                        )}
                      </div>

                      {/* Riepiloghi Caratteristiche (sezioni "Cosa ci contraddistingue" nel Portale Cliente) */}
                      {[
                        { field: "riepilogo_caratteristiche_pvc", nome: "PVC", testid: "pvc" },
                        { field: "riepilogo_caratteristiche_porte", nome: "Porte Interne", testid: "porte" },
                      ].map(({ field, nome, testid }) => (
                        <label key={field} className="col-span-2 flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid={`detail-riepilogo-${testid}-label`}>
                          <input
                            type="checkbox"
                            className="w-4 h-4 accent-emerald-600"
                            checked={!!selectedClientDetail[field]}
                            onChange={(e) => handleToggleRiepilogo(selectedClientDetail.id, field, e.target.checked, nome)}
                            data-testid={`detail-riepilogo-${testid}-checkbox`}
                          />
                          Riepilogo Caratteristiche {nome}
                          <span className="text-xs text-slate-400">(mostra "Cosa ci contraddistingue" nel Portale Cliente)</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 pt-4">
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleNotificaOfferta(selectedClientDetail.id)}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Notifica Offerta
                      </Button>
                      <Button 
                        onClick={handleAggiungiAlFunnel}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Aggiungi al Funnel
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setIsClientDetailOpen(false)} className="flex-1">
                        Annulla
                      </Button>
                      <Button onClick={handleSaveClientDetail} className="flex-1 bg-[#1e40af] hover:bg-[#1e3a8a] text-white">
                        Salva Modifiche
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* ==================== PREVIEW EMAIL MODAL (Notifica Offerta) ==================== */}
          <Dialog open={isPreviewOpen} onOpenChange={(open) => { if (!previewSending) setIsPreviewOpen(open); }}>
            <DialogContent
              data-testid="email-preview-modal"
              className="max-w-5xl w-[95vw] h-[92vh] p-0 flex flex-col overflow-hidden"
            >
              <DialogHeader className="px-6 pt-5 pb-3 border-b bg-slate-50 shrink-0">
                <DialogTitle className="flex items-center gap-2 text-lg">
                  <Eye className="w-5 h-5 text-blue-600" />
                  Anteprima Email — Notifica Offerta
                </DialogTitle>
                <div className="text-sm text-slate-600 pt-2 space-y-0.5">
                  <div><span className="font-medium">A:</span> {previewTo || "—"}</div>
                  <div><span className="font-medium">BCC:</span> {previewBcc || "—"}</div>
                  <div><span className="font-medium">Oggetto:</span> {previewSubject || "—"}</div>
                </div>
              </DialogHeader>

              <div className="flex-1 min-h-0 overflow-hidden bg-slate-100">
                {previewLoading ? (
                  <div className="flex items-center justify-center h-full text-slate-500">
                    <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                    Caricamento anteprima...
                  </div>
                ) : previewEditMode ? (
                  <div className="h-full overflow-auto p-6 space-y-4 bg-white" data-testid="email-preview-edit-panel">
                    <div>
                      <Label htmlFor="edit-subject">Oggetto</Label>
                      <Input
                        id="edit-subject"
                        data-testid="email-preview-edit-subject"
                        value={previewSubject}
                        onChange={(e) => setPreviewSubject(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-html">Corpo HTML</Label>
                      <Textarea
                        id="edit-html"
                        data-testid="email-preview-edit-html"
                        value={previewHtml}
                        onChange={(e) => setPreviewHtml(e.target.value)}
                        rows={20}
                        className="mt-1 font-mono text-xs"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-text">Fallback plain-text (opzionale)</Label>
                      <Textarea
                        id="edit-text"
                        data-testid="email-preview-edit-text"
                        value={previewText}
                        onChange={(e) => setPreviewText(e.target.value)}
                        rows={8}
                        className="mt-1 font-mono text-xs"
                      />
                    </div>
                  </div>
                ) : (
                  <iframe
                    title="email-preview"
                    data-testid="email-preview-iframe"
                    srcDoc={previewHtml}
                    sandbox=""
                    className="w-full h-full border-0 bg-white"
                  />
                )}
              </div>

              <div className="border-t bg-white px-6 py-3 flex flex-wrap items-center justify-between gap-2 shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  data-testid="email-preview-toggle-edit"
                  onClick={() => setPreviewEditMode((v) => !v)}
                  disabled={previewLoading || previewSending}
                >
                  {previewEditMode ? (
                    <><Eye className="w-4 h-4 mr-2" /> Torna all'anteprima</>
                  ) : (
                    <><Edit className="w-4 h-4 mr-2" /> Modifica testo</>
                  )}
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="email-preview-cancel"
                    onClick={() => setIsPreviewOpen(false)}
                    disabled={previewSending}
                  >
                    Annulla
                  </Button>
                  <Button
                    type="button"
                    data-testid="email-preview-confirm-send"
                    onClick={handleConfirmSendOfferta}
                    disabled={previewLoading || previewSending || !previewHtml}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    {previewSending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Conferma e invia</>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
    </PageLayout>
  );
}
